import { useEffect, useState } from 'react'
import { endpointLabels } from '../../../common/utils/enum'
import {
  formatDecimals,
  getEndpointFromConfig,
} from '../../../common/utils/helper'
import fetchData from '../../../service/apiService'
import { useSelector } from 'react-redux'
import ForegroundLoader from '../../../common/components/Loader/ForegroundLoader'
import { TOPIC_LIST } from '../../../common/utils/constants'
import SingleCircle from './SingleCircle'

const PillarGrid = ({ countryProfileURLAuthor, countryProfileURLPublish }) => {
  const { endpointList, environment, apiKeyIncluded, apiKeyName, apiKeyValue } =
    useSelector((state) => state.common)
  const { selectedPillar, countryList } = useSelector(
    (state) => state.dataHomepage
  )

  const [isLoading, setIsLoading] = useState(false)
  const [gridData, setGridData] = useState([])
  const [availableColumns, setAvailableColumns] = useState(
    TOPIC_LIST.map((item) => item.name)
  )
  const [sortConfig, setSortConfig] = useState({
    key: 'EconomyName',
    order: 'asc',
  })
  const [isHovered, setIsHovered] = useState({})

  useEffect(() => {
    const fetchPillarData = async () => {
      try {
        setIsLoading(true)
        const url = `${
          getEndpointFromConfig(
            endpointList,
            endpointLabels.PillarScoresForTopics
          )?.apiEndpoint
        }?Pillar=${selectedPillar}`

        let headers = undefined
        if (apiKeyIncluded === 'true') {
          headers = {
            [apiKeyName]: apiKeyValue,
          }
        }
        const response = await fetchData(url, 'GET', null, headers)

        if (response && response.length > 0) {
          let finalDataForGrid = response.map((item) => {
            return {
              ...item,
              ['Business Entry']: item['Business Entry']
                ? formatDecimals(item['Business Entry'])
                : '',
              ['Business Insolvency']: item['Business Insolvency']
                ? formatDecimals(item['Business Insolvency'])
                : '',
              ['Business Location']: item['Business Location']
                ? formatDecimals(item['Business Location'])
                : '',
              ['Dispute Resolution']: item['Dispute Resolution']
                ? formatDecimals(item['Dispute Resolution'])
                : '',
              ['Financial Services']: item['Financial Services']
                ? formatDecimals(item['Financial Services'])
                : '',
              ['International Trade']: item['International Trade']
                ? formatDecimals(item['International Trade'])
                : '',
              ['Labor']: item['Labor'] ? formatDecimals(item['Labor']) : '',
              ['Market Competition']: item['Market Competition']
                ? formatDecimals(item['Market Competition'])
                : '',
              ['Taxation']: item['Taxation']
                ? formatDecimals(item['Taxation'])
                : '',
              ['Utility Services']: item['Utility Services']
                ? formatDecimals(item['Utility Services'])
                : '',
              OverallPillarScore: formatDecimals(item.OverallPillarScore),
            }
          })
          finalDataForGrid.sort((a, b) =>
            a.EconomyName.toLowerCase().localeCompare(
              b.EconomyName.toLowerCase()
            )
          )
          setGridData(finalDataForGrid)
        }
      } catch (error) {
        console.error('Error fetching specific pillar data for topics:', error)
      } finally {
        setIsLoading(false)
      }
    }
    if (selectedPillar && endpointList.length > 0 && apiKeyIncluded) {
      fetchPillarData()
    }
  }, [selectedPillar, endpointList, apiKeyIncluded])

  const handleSorting = (key) => {
    let sortedData = [...gridData]
    if (key === 'EconomyName') {
      if (sortConfig?.key === key && sortConfig?.order === 'asc') {
        sortedData.reverse()
        setSortConfig({ key, order: 'desc' })
      } else {
        sortedData.sort((a, b) =>
          a[key].toLowerCase().localeCompare(b[key].toLowerCase())
        )
        setSortConfig({ key, order: 'asc' })
      }
    } else {
      if (sortConfig?.key === key && sortConfig?.order === 'desc') {
        sortedData.sort((a, b) => {
          if (a[key] === null || a[key] === undefined || a[key] === '') return 1
          if (b[key] === null || b[key] === undefined || b[key] === '')
            return -1
          return Number(a[key]) - Number(b[key])
        })
        setSortConfig({ key, order: 'asc' })
      } else {
        sortedData.sort((a, b) => {
          if (a[key] === null || a[key] === undefined || a[key] === '') return 1
          if (b[key] === null || b[key] === undefined || b[key] === '')
            return -1
          return Number(b[key]) - Number(a[key])
        })
        setSortConfig({ key, order: 'desc' })
      }
    }
    setGridData(sortedData)
  }

  const tableRows =
    gridData &&
    gridData.length > 0 &&
    gridData.map((item) => {
      const countryData = countryList.find(
        (country) => country.id.toLowerCase() === item.EconomyCode.toLowerCase()
      )
      let redirectURL = ''
      if (environment === 'author') {
        redirectURL = `${countryProfileURLAuthor}.${countryData?.url}.html`
      } else {
        redirectURL = `${countryProfileURLPublish}/${countryData?.url}`
      }
      return (
        <tr key={`pillar-row-${item.EconomyCode}`}>
          <td>
            <a href={redirectURL}>{item.EconomyName}</a>
          </td>
          <td style={{ position: 'relative' }}>
            {TOPIC_LIST.map((topic) => {
              return (
                availableColumns.includes(topic.name) && (
                  <div key={`pillar-circle-${item.EconomyCode}-${topic.id}`}>
                    <SingleCircle
                      value={item[topic.name]}
                      countryCode={item.EconomyCode}
                      topic={topic.id}
                    />
                  </div>
                )
              )
            })}
          </td>
          <td>{item.OverallPillarScore}</td>
        </tr>
      )
    })

  return (
    <div className="bready-table">
      {isLoading && <ForegroundLoader />}
      {selectedPillar && (
        <table>
          <thead>
            <tr>
              <th
                onClick={() => handleSorting('EconomyName')}
                className={
                  sortConfig.key === 'EconomyName'
                    ? sortConfig.order === 'asc'
                      ? 'sort-asc'
                      : 'sort-desc'
                    : 'sort-default'
                }
              >
                Economy
              </th>
              <th>Topics</th>
              <th
                onClick={() => handleSorting('OverallPillarScore')}
                className={
                  sortConfig.key === 'OverallPillarScore'
                    ? sortConfig.order === 'asc'
                      ? 'sort-asc'
                      : 'sort-desc'
                    : 'sort-default'
                }
              >
                Pillar Score
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              <td>
                {TOPIC_LIST.map((item) => {
                  return (
                    availableColumns.includes(item.name) && (
                      <div
                        style={{ position: 'relative', margin: '0px' }}
                        className={
                          sortConfig.key === item.name
                            ? sortConfig.order === 'asc'
                              ? 'sort-asc'
                              : 'sort-desc'
                            : 'sort-default'
                        }
                      >
                        <span
                          onClick={() => handleSorting(item.name)}
                          key={`topic-column-${item.id}`}
                          onMouseEnter={() => setIsHovered({ [item.id]: true })}
                          onMouseLeave={() =>
                            setIsHovered({ [item.id]: false })
                          }
                        >
                          <i
                            className={`lp ${item.icon}`}
                            style={{
                              background: '#009FDA',
                              fontSize: '1.35rem',
                              lineHeight: '2.5rem',
                              borderRadius: '100%',
                              padding: '4px',
                              marginRight: '10px',
                            }}
                          ></i>
                        </span>
                        {isHovered && isHovered[item.id] && (
                          <div className="pillar-topic-tooltip">
                            {item.name}
                          </div>
                        )}
                      </div>
                    )
                  )
                })}
              </td>
              <td></td>
            </tr>
            {tableRows}
          </tbody>
        </table>
      )}
    </div>
  )
}
export default PillarGrid
